import React from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';

/**
 * Small info icon with an app-styled tooltip (same look as ClampedTextWithTooltip) instead of the
 * browser's native `title` bubble. Shows on hover and keyboard focus.
 */
export function InfoTooltip({ text, className = '' }: { text: string; className?: string }) {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number; placement: 'above' | 'below' } | null>(null);
  const tipId = React.useId();

  const show = React.useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const placement: 'above' | 'below' = rect.top > 80 ? 'above' : 'below';
    setPos({
      top: placement === 'below' ? rect.bottom + gap : rect.top - gap,
      left: rect.left + rect.width / 2,
      placement,
    });
  }, []);
  const hide = React.useCallback(() => setPos(null), []);

  React.useEffect(() => {
    if (!pos) return;
    window.addEventListener('scroll', hide, true);
    window.addEventListener('resize', hide);
    return () => {
      window.removeEventListener('scroll', hide, true);
      window.removeEventListener('resize', hide);
    };
  }, [pos, hide]);

  return (
    <>
      <span
        ref={anchorRef}
        tabIndex={0}
        aria-label={text}
        aria-describedby={pos ? tipId : undefined}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`inline-flex cursor-help rounded-full text-app-text-subtle outline-none hover:text-app-text-muted focus-visible:ring-2 focus-visible:ring-pipedrive-blue/30 ${className}`.trim()}
      >
        <Info className="h-3.5 w-3.5" />
      </span>
      {pos && typeof document !== 'undefined'
        ? createPortal(
            <div
              id={tipId}
              role="tooltip"
              // Above modals and drawers (fullscreen modals sit at 10100).
              className="pointer-events-none fixed z-[10200] w-max max-w-[min(280px,calc(100vw-24px))] rounded-lg border border-app-border bg-app-surface px-3 py-2 text-xs font-normal leading-relaxed text-app-text-muted shadow-lg"
              style={{
                top: pos.top,
                left: pos.left,
                transform: `translateX(-50%)${pos.placement === 'above' ? ' translateY(-100%)' : ''}`,
              }}
            >
              {text}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
